/**
 * Additive panels on a PF job (fieldiq-api#1227, PF-4). JobSections.tsx is not
 * edited; these sit beside it.
 *
 *   PfJobTicketsSection  the job's tickets, and "Create ticket" — per job, or
 *                        per day with a date when the job is ticketed per day.
 *                        Drafting reads the daily logs; a refusal (no logs,
 *                        a ticket already, the other scope) is a sentence.
 *   PfRentInBillingPanel the billing rate of each rent-in: rented-in-equipment
 *                        rates only, from this job's customer card or the
 *                        default card (`rate_not_rent_in`, `rate_other_customer`).
 */
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCreatePfTicket, usePfRates, usePfRentInBillingRates, usePfTickets, useSetPfRentInBillingRate } from '../../../hooks/usePfTickets';
import { queryLoadFailure } from '../../../lib/load-failure';
import { pfScopeText, pfTicketRefusal, pfTicketStatusSkin } from '../../../lib/pf-tickets';
import type { PfJob, PfRentIn } from '../../../types/pipeline-facility';
import type { PfTicket } from '../../../types/pf-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { PF_DATE_INPUT, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfBadge, PfEmpty, PfField, PfFormError, PfPanel, PfRegister, type PfColumn } from '../PfBits';
import { pfTicketTotalText } from './TicketsScreen';
import { pfRateOption } from './TicketBits';

const columns: PfColumn<PfTicket>[] = [
  { key: 'number', header: 'Ticket', render: (t) => <span className="font-mono font-extrabold text-white">{t.ticketNumber || '—'}</span> },
  { key: 'scope', header: 'Covers', render: (t) => pfScopeText(t) },
  { key: 'lines', header: 'Lines', align: 'right', render: (t) => String(t.lineCount ?? '—') },
  { key: 'status', header: 'Status', render: (t) => <PfBadge skin={pfTicketStatusSkin(t.status)} value={t.status} /> },
  { key: 'total', header: 'Total', align: 'right', render: (t) => <span className="font-mono">{pfTicketTotalText(t)}</span> },
];

export function PfJobTicketsSection({ job, canWrite, today }: { job: PfJob; canWrite: boolean; today: string | null }) {
  const openRecord = useOpenRecord();
  const query = usePfTickets({ job_id: job.id });
  const create = useCreatePfTicket();
  const perDay = job.ticketMode === 'per_day';
  const [creating, setCreating] = useState(false);
  const [day, setDay] = useState(today ?? '');
  const [error, setError] = useState<string | null>(null);
  const tickets = query.data?.data ?? [];

  const draft = () => {
    setError(null);
    if (perDay && !/^\d{4}-\d{2}-\d{2}$/.test(day)) return setError('This job is ticketed per day. Pick the day to ticket.');
    create.mutate(
      { jobId: job.id, workDate: perDay ? day : undefined },
      {
        onSuccess: (r) => {
          toast.success(r.data ? `${r.data.ticketNumber} drafted from the daily logs` : 'Ticket drafted');
          setCreating(false);
          if (r.data) openRecord(`/pipeline-facility/tickets/${r.data.id}`);
        },
        onError: (e) => setError(pfTicketRefusal(e, 'The ticket could not be drafted.')),
      },
    );
  };

  return (
    <PfPanel
      eyebrow="Field tickets"
      title={perDay ? 'Ticketed per day' : 'Ticketed per job'}
      testId="pf-job-tickets"
      actions={
        canWrite && !creating ? (
          <button type="button" className={PF_PRIMARY} onClick={() => { setError(null); setCreating(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Create ticket
          </button>
        ) : undefined
      }
    >
      {creating && (
        <div data-testid="pf-job-ticket-create" className="space-y-3 border-b border-fieldiq-border px-4 py-4">
          <p className="text-sm text-fieldiq-text">
            {perDay
              ? 'Drafts one ticket for the day you pick, from that day’s daily log. Each line is priced from the rate card where a rate fits.'
              : 'Drafts one ticket covering every day of the job, from its daily logs. Each line is priced from the rate card where a rate fits.'}
          </p>
          <PfFormError message={error} />
          {perDay && (
            <div className="grid gap-3 sm:grid-cols-[14rem]">
              <PfField label="Day to ticket *">
                <input className={PF_DATE_INPUT} type="date" aria-label="Day to ticket" value={day} onChange={(e) => setDay(e.target.value)} />
              </PfField>
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={PF_SECONDARY} onClick={() => setCreating(false)} disabled={create.isPending}>
              Cancel
            </button>
            <button type="button" className={PF_PRIMARY} onClick={draft} disabled={create.isPending}>
              {create.isPending ? 'Drafting…' : 'Draft ticket'}
            </button>
          </div>
        </div>
      )}
      <PfRegister
        query={query}
        rows={tickets}
        columns={columns}
        rail={(t) => pfTicketStatusSkin(t.status).rail}
        onOpen={(t) => openRecord(`/pipeline-facility/tickets/${t.id}`)}
        subject="field tickets"
        testId="pf-job-ticket"
        minWidth="min-w-[640px]"
        empty={<PfEmpty>No tickets for this job yet.</PfEmpty>}
        card={(t) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{t.ticketNumber}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">{pfScopeText(t)} · Total {pfTicketTotalText(t)}</p>
            </div>
            <PfBadge skin={pfTicketStatusSkin(t.status)} value={t.status} />
          </div>
        )}
      />
    </PfPanel>
  );
}

function RentInRateRow({
  rentIn,
  current,
  loading,
  failed,
  options,
  canWrite,
}: {
  rentIn: PfRentIn;
  current: { id: string; label: string } | null;
  loading: boolean;
  failed: boolean;
  options: { id: string; label: string }[];
  canWrite: boolean;
}) {
  const set = useSetPfRentInBillingRate();
  const [error, setError] = useState<string | null>(null);
  return (
    <li data-testid="pf-rent-in-billing-row" className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-white">{rentIn.description || '—'}</p>
          <p className="text-xs text-fieldiq-muted">
            {rentIn.vendorName || '—'}
            {rentIn.unitNumber ? ` · ${rentIn.unitNumber}` : ''}
          </p>
        </div>
        <div className="w-full sm:w-[28rem]">
          {canWrite ? (
            <select
              className={PF_INPUT}
              aria-label={`Billing rate for ${rentIn.description}`}
              value={current?.id ?? ''}
              disabled={loading || failed || set.isPending}
              onChange={(e) => {
                setError(null);
                set.mutate(
                  { rentInId: rentIn.id, rateId: e.target.value || null },
                  {
                    onSuccess: () => toast.success(e.target.value ? 'Billing rate set' : 'Billing rate cleared'),
                    onError: (err) => setError(pfTicketRefusal(err, 'The billing rate could not be set.')),
                  },
                );
              }}
            >
              <option value="">{loading ? 'Loading…' : 'Not billed from the rate card'}</option>
              {current && !options.some((o) => o.id === current.id) && <option value={current.id}>{current.label}</option>}
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-fieldiq-text">{loading ? '…' : current?.label ?? '—'}</p>
          )}
        </div>
      </div>
      {failed && <PfFormError message="This rent-in's billing rate could not be read." />}
      <PfFormError message={error} />
    </li>
  );
}

export function PfRentInBillingPanel({ job, rentIns, canWrite }: { job: PfJob; rentIns: PfRentIn[]; canWrite: boolean }) {
  const ratesQuery = usePfRates({ kind: 'rent_in' }, canWrite);
  const billing = usePfRentInBillingRates(rentIns.map((r) => r.id));
  const options = (ratesQuery.data?.data ?? [])
    .filter((r) => r.kind === 'rent_in' && (r.customerId === null || r.customerId === job.customerId))
    .map((r) => ({ id: r.id, label: pfRateOption(r) }));
  return (
    <PfPanel eyebrow="Billing" title="Rent-in billing rates" testId="pf-rent-in-billing">
      <p className="border-b border-fieldiq-border px-4 py-3 text-sm text-fieldiq-muted">
        What the customer is billed for each rent-in, from the rate card — rented-in equipment rates on this customer’s card or the default card. A rent-in with no
        billing rate drafts onto a ticket unpriced.
      </p>
      {canWrite && queryLoadFailure(ratesQuery) && <div className="px-4 pt-3"><PfFormError message="The rate card could not be read, so a billing rate cannot be picked right now." /></div>}
      {!rentIns.length ? (
        <PfEmpty>No rent-ins on this job.</PfEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {rentIns.map((ri, i) => {
            const q = billing[i];
            const rate = q?.data?.data?.rate ?? null;
            return (
              <RentInRateRow
                key={ri.id}
                rentIn={ri}
                current={rate ? { id: rate.id, label: pfRateOption(rate) } : null}
                loading={!!q?.isLoading}
                failed={!!q && !!queryLoadFailure(q)}
                options={options}
                canWrite={canWrite}
              />
            );
          })}
        </ul>
      )}
    </PfPanel>
  );
}

